import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { classMap } from 'lit/directives/class-map.js';
import { watch } from '../../internal/watch.js';
import { emit } from '../../internal/event.js';
import { FormController } from '../../internal/form-control.js';
import { ARC_EVENTS } from '../../internal/constants/eventConstants.js';
import styles from './arc-radio.styles.js';
export default class ArcRadio extends LitElement {
    constructor() {
        super(...arguments);
        this.formController = new FormController(this, {
            value: (control) => (control.checked ? control.value : undefined),
        });
        this.disabled = false;
        this.checked = false;
    }
    firstUpdated() {
        this.updateComplete.then(() => {
            const radios = this.getAllRadios();
            const checkedRadio = radios.find(radio => radio.checked);
            radios.forEach(radio => {
                radio.input.tabIndex = -1;
            });
            if (checkedRadio && !checkedRadio.disabled) {
                checkedRadio.input.tabIndex = 0;
            }
            else if (radios.length > 0) {
                const enabledRadios = this.getAllRadios({ includeDisabled: false });
                enabledRadios[0].input.tabIndex = 0;
            }
        });
    }
    handleCheckedChange() {
        if (this.checked) {
            this.input.tabIndex = 0;
            this.getSiblingRadios().forEach(radio => {
                radio.input.tabIndex = -1;
                radio.checked = false;
            });
        }
    }
    getAllRadios(options = { includeDisabled: true }) {
        const radioGroup = this.closest('arc-radio-group');
        const { includeDisabled } = options;
        if (!radioGroup)
            return [this];
        return [...radioGroup.querySelectorAll('arc-radio')].filter((radio) => {
            if (radio.name !== this.name)
                return false;
            return !(!includeDisabled && radio.disabled);
        });
    }
    getSiblingRadios() {
        return this.getAllRadios().filter(radio => radio !== this);
    }
    click() {
        this.input.click();
    }
    focus(options) {
        this.input.focus(options);
    }
    blur() {
        this.input.blur();
    }
    _handleClick() {
        if (!this.checked) {
            this.checked = true;
            emit(this, ARC_EVENTS.change);
        }
    }
    handleKeyDown(event) {
        if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
            const radios = this.getAllRadios({ includeDisabled: false });
            const incr = ['ArrowUp', 'ArrowLeft'].includes(event.key) ? -1 : 1;
            let index = radios.indexOf(this) + incr;
            if (index < 0)
                index = radios.length - 1;
            if (index > radios.length - 1)
                index = 0;
            this.getAllRadios().forEach(radio => {
                radio.checked = false;
                radio.input.tabIndex = -1;
            });
            radios[index].input.focus();
            radios[index].checked = true;
            radios[index].input.tabIndex = 0;
            emit(radios[index], ARC_EVENTS.change);
            event.preventDefault();
        }
    }
    render() {
        return html `
      <label
        id="main"
        class=${classMap({
            radio: true,
            'radio--checked': this.checked,
            'radio--disabled': this.disabled,
        })}
        @keydown=${this.handleKeyDown}
      >
        <input
          type="radio"
          name=${ifDefined(this.name || undefined)}
          .value=${ifDefined(this.value || undefined)}
          .checked=${live(this.checked)}
          .disabled=${this.disabled}
          aria-checked=${this.checked}
          aria-disabled=${this.disabled}
          @click=${this._handleClick}
        />
        <span id="control">
          <span id="icon">
            <svg
              class="bg"
              focusable="false"
              aria-hidden="true"
              viewBox="0 0 24 24"
              data-testid="RadioButtonUncheckedIcon"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"
              ></path>
            </svg>
            <svg
              class="fill"
              focusable="false"
              aria-hidden="true"
              viewBox="0 0 24 24"
              data-testid="RadioButtonCheckedIcon"
            >
              <path
                d="M8.465 8.465C9.37 7.56 10.62 7 12 7C14.76 7 17 9.24 17 12C17 13.38 16.44 14.63 15.535 15.535C14.63 16.44 13.38 17 12 17C9.24 17 7 14.76 7 12C7 10.62 7.56 9.37 8.465 8.465Z"
              ></path>
            </svg>
          </span>
        </span>
        <span id="label"><slot></slot></span>
      </label>
    `;
    }
}
ArcRadio.tag = 'arc-radio';
ArcRadio.styles = styles;
__decorate([
    query('input[type="radio"]')
], ArcRadio.prototype, "input", void 0);
__decorate([
    property({ type: String })
], ArcRadio.prototype, "name", void 0);
__decorate([
    property({ type: String })
], ArcRadio.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcRadio.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcRadio.prototype, "checked", void 0);
__decorate([
    watch('checked', { waitUntilFirstUpdate: true })
], ArcRadio.prototype, "handleCheckedChange", null);
